/**
 * Notification Utilities
 * Core notification logic for toast, badge, and FCM integration
 */

import DOMPurify from 'dompurify';
import { auth } from '../core/firebase-config';
import logger from '../core/logger';
import { isNative } from '../core/platform';

/**
 * @typedef {'info' | 'success' | 'warning' | 'error' | 'insight'} NotificationType
 */

/**
 * @typedef {Object} NotificationOptions
 * @property {NotificationType} type - Notification type
 * @property {number} duration - Auto-dismiss duration (ms)
 * @property {boolean} dismissible - User can dismiss
 * @property {Function} onClick - Click handler
 * @property {string} icon - Custom icon/emoji
 * @property {string} cta - Call-to-action text
 */

// Notification counters for throttling
const notificationCounts = {
  daily: 0,
  session: 0,
  lastReset: Date.now()
};

// Deduplication: suppress identical messages within a short window
const recentMessages = new Map();
const DEDUP_WINDOW_MS = 1500;

// Constants
const MAX_DAILY_NOTIFICATIONS = 5;
const MAX_SESSION_NOTIFICATIONS = 3;
const QUIET_HOURS_START = 23; // 11pm
const QUIET_HOURS_END = 5; // 5am
const RESET_INTERVAL = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Check if currently in quiet hours
 * @param {Date} date - Date to check (defaults to now)
 * @returns {boolean}
 */
export function isQuietHours(date = new Date()) {
  const hour = date.getHours();

  if (QUIET_HOURS_START < QUIET_HOURS_END) {
    return hour >= QUIET_HOURS_START && hour < QUIET_HOURS_END;
  } else {
    // Handles overnight quiet hours (e.g., 22:00 - 08:00)
    return hour >= QUIET_HOURS_START || hour < QUIET_HOURS_END;
  }
}

/**
 * Check if notification throttle allows sending
 * @param {NotificationType} type - Type of notification to check
 * @returns {{allowed: boolean, reason?: string}}
 */
export function checkNotificationThrottle(type = 'info') {
  // Only throttle 'insight' notifications - allow all user feedback notifications
  if (type !== 'insight') {
    return { allowed: true };
  }

  // Reset counters if 24 hours passed
  if (Date.now() - notificationCounts.lastReset > RESET_INTERVAL) {
    notificationCounts.daily = 0;
    notificationCounts.session = 0;
    notificationCounts.lastReset = Date.now();
  }

  // Check quiet hours
  if (isQuietHours()) {
    return {
      allowed: false,
      reason: 'Quiet hours active (10pm - 8am)'
    };
  }

  // Check daily limit
  if (notificationCounts.daily >= MAX_DAILY_NOTIFICATIONS) {
    return {
      allowed: false,
      reason: 'Daily notification limit reached'
    };
  }

  // Check session limit
  if (notificationCounts.session >= MAX_SESSION_NOTIFICATIONS) {
    return {
      allowed: false,
      reason: 'Session notification limit reached'
    };
  }

  return { allowed: true };
}

/**
 * Increment notification counters
 */
function incrementNotificationCount() {
  notificationCounts.daily++;
  notificationCounts.session++;
}

/**
 * Show enhanced toast notification
 * @param {string} message - Notification message
 * @param {NotificationOptions} options - Notification options
 */
export function showNotification(message, options = {}) {
  const {
    type = 'info',
    duration = 5000,
    dismissible = true,
    onClick = null,
    icon = null,
    cta = null
  } = options;

  // Deduplicate: suppress identical messages within a short window
  const dedupKey = `${type}:${message}`;
  const lastShown = recentMessages.get(dedupKey);
  if (lastShown && Date.now() - lastShown < DEDUP_WINDOW_MS) {
    return;
  }
  recentMessages.set(dedupKey, Date.now());

  // Check throttle (only throttles 'insight' type notifications)
  const throttle = checkNotificationThrottle(type);
  if (!throttle.allowed) {
    logger.debug('[Notifications] Throttled:', throttle.reason);
    return;
  }

  // Increment counter
  incrementNotificationCount();

  // Get or create container
  let container = document.getElementById('notification-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'notification-container';
    document.body.appendChild(container);
  }

  // Always update container styles (fixes cached old positioning on mobile)
  container.className = 'fixed right-4 z-[100] space-y-2 max-w-sm';
  container.style.bottom = 'calc(76px + env(safe-area-inset-bottom, 0px))';

  // Create notification element
  const notification = document.createElement('div');
  notification.className = `
    flex items-center gap-3 px-4 py-3 rounded-2xl
    transform transition-all duration-500 ease-out
    ${getNotificationStyles(type)}
    ${onClick || cta ? 'cursor-pointer active:scale-95' : ''}
  `;

  // Start off-screen
  notification.style.transform = 'translateX(400px)';
  notification.style.opacity = '0';

  // Get icon
  const displayIcon = icon || getNotificationIcon(type);

  // Build HTML - cleaner, more minimal design
  // Sanitize all interpolated values since message can originate from FCM payloads
  const safeIcon = DOMPurify.sanitize(displayIcon);
  const safeMessage = DOMPurify.sanitize(message);
  const safeCta = cta ? DOMPurify.sanitize(cta) : '';
  notification.innerHTML = `
    <div class="flex-shrink-0 text-xl opacity-90">${safeIcon}</div>
    <div class="flex-1 min-w-0">
      <p class="text-sm font-light leading-relaxed">${safeMessage}</p>
      ${safeCta ? `<button class="mt-1.5 text-xs font-normal opacity-80 hover:opacity-100 transition-opacity">${safeCta} →</button>` : ''}
    </div>
    ${dismissible ? `
      <button class="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors opacity-60 hover:opacity-100 text-base" aria-label="Dismiss">×</button>
    ` : ''}
  `;

  // Add click handler
  if (onClick) {
    notification.addEventListener('click', (e) => {
      if (!e.target.closest('button')) {
        onClick();
        removeNotification(notification);
      }
    });
  }

  // Add dismiss handler
  if (dismissible) {
    const dismissBtn = notification.querySelector('button[aria-label="Dismiss"]');
    if (dismissBtn) {
      dismissBtn.addEventListener('click', () => {
        removeNotification(notification);
      });
    }
  }

  // Add to container
  container.appendChild(notification);

  // Animate in
  requestAnimationFrame(() => {
    notification.style.transform = 'translateX(0)';
    notification.style.opacity = '1';
  });

  // Auto-dismiss
  if (duration > 0) {
    setTimeout(() => {
      removeNotification(notification);
    }, duration);
  }

  return notification;
}

/**
 * Remove notification with animation
 * @param {HTMLElement} notification - Notification element
 */
function removeNotification(notification) {
  if (!notification || !notification.parentElement) return;

  notification.style.transform = 'translateX(400px)';
  notification.style.opacity = '0';

  setTimeout(() => {
    notification.remove();

    // Clean up container if empty
    const container = document.getElementById('notification-container');
    if (container && container.children.length === 0) {
      container.remove();
    }
  }, 300);
}

/**
 * Get notification styles based on type
 * @param {NotificationType} type - Notification type
 * @returns {string} CSS classes
 */
function getNotificationStyles(type) {
  const styles = {
    // Clean amber with subtle gradient - matches app's minimalist aesthetic
    info: 'bg-[#1a1410]/95 backdrop-blur-xl border-l-2 border-l-[#ffb85e] text-white shadow-2xl',

    // Success with teal accent - clean and minimal
    success: 'bg-[#1a1410]/95 backdrop-blur-xl border-l-2 border-l-[#7ecec0] text-white shadow-2xl',

    // Warning with warm amber - subtle and clean
    warning: 'bg-[#1a1410]/95 backdrop-blur-xl border-l-2 border-l-[#ffb85e] text-white shadow-2xl',

    // Error with coral accent - soft but noticeable
    error: 'bg-[#1a1410]/95 backdrop-blur-xl border-l-2 border-l-[#ff8e7e] text-white shadow-2xl',

    // Insight with purple accent - elegant and minimal
    insight: 'bg-[#1a1410]/95 backdrop-blur-xl border-l-2 border-l-[#c9a0dc] text-white shadow-2xl'
  };

  return styles[type] || styles.info;
}

/**
 * Get notification icon based on type
 * @param {NotificationType} type - Notification type
 * @returns {string} Icon emoji
 */
function getNotificationIcon(type) {
  const icons = {
    info: '○',
    success: '✓',
    warning: '!',
    error: '✕',
    insight: '✦'
  };

  return icons[type] || icons.info;
}

/**
 * Show insight notification (specialized)
 * @param {Object} insight - Insight object
 * @param {Function} onView - Callback to view insight
 */
export function showInsightNotification(insight, onView) {
  const message = generateInsightMessage(insight);

  showNotification(message, {
    type: 'insight',
    duration: 7000,
    cta: 'View',
    onClick: () => {
      if (onView) {
        onView(insight);
      }
    }
  });
}

/**
 * Generate personalized insight notification message
 * @param {Object} insight - Insight object
 * @returns {string} Notification message
 */
function generateInsightMessage(insight) {
  const { type, confidence } = insight;

  // High confidence, more direct
  if (confidence >= 0.9) {
    const messages = {
      milestone: 'New milestone reached',
      pattern: 'Pattern detected in your entries',
      reflection: 'Reflection from your recent entries',
      suggestion: 'Something to explore'
    };
    return messages[type] || messages.reflection;
  }

  // Medium confidence, more gentle
  const messages = {
    milestone: 'Possible milestone',
    pattern: 'Interesting pattern emerged',
    reflection: 'Gentle reflection',
    suggestion: 'Something to consider'
  };

  return messages[type] || messages.reflection;
}

/**
 * Request browser notification permission
 * @returns {Promise<NotificationPermission>}
 */
export async function requestNotificationPermission() {
  // Native push goes through Capacitor (nativePush.js) — web Notification API not applicable
  if (isNative()) return 'denied';

  if (!('Notification' in window)) {
    throw new Error('This browser does not support notifications');
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    return 'denied';
  }

  const permission = await Notification.requestPermission();
  return permission;
}

/**
 * Check if browser notifications are available
 * @returns {boolean}
 */
export function hasNotificationSupport() {
  return 'Notification' in window;
}

/**
 * Get current notification permission status
 * @returns {NotificationPermission}
 */
export function getNotificationPermission() {
  if (!hasNotificationSupport()) {
    return 'denied';
  }

  return Notification.permission;
}

/**
 * Badge counter management
 */
export const NotificationBadge = {
  count: 0,

  /**
   * Set badge count
   * @param {number} count - Badge count
   */
  set(count) {
    this.count = Math.max(0, count);
    this.update();
  },

  /**
   * Increment badge count
   * @param {number} amount - Amount to increment
   */
  increment(amount = 1) {
    this.count += amount;
    this.update();
  },

  /**
   * Decrement badge count
   * @param {number} amount - Amount to decrement
   */
  decrement(amount = 1) {
    this.count = Math.max(0, this.count - amount);
    this.update();
  },

  /**
   * Clear badge
   */
  clear() {
    this.count = 0;
    this.update();
  },

  /**
   * Get current count
   * @returns {number}
   */
  get() {
    return this.count;
  },

  /**
   * Update UI badges
   */
  update() {
    // Update all badge elements
    const badges = document.querySelectorAll('[data-notification-badge]');
    badges.forEach(badge => {
      if (this.count > 0) {
        badge.textContent = this.count > 99 ? '99+' : this.count;
        badge.classList.remove('hidden');
      } else {
        badge.classList.add('hidden');
      }
    });

    // Update app badge API if supported
    if ('setAppBadge' in navigator) {
      if (this.count > 0) {
        navigator.setAppBadge(this.count).catch(err => logger.error('setAppBadge failed:', err));
      } else {
        navigator.clearAppBadge().catch(err => logger.error('clearAppBadge failed:', err));
      }
    }
  }
};

/**
 * Track user's active times for smart notification timing
 */
export const ActivityTracker = {
  sessions: [],

  /**
   * Record user activity
   */
  recordActivity() {
    const now = new Date();
    const hour = now.getHours();

    this.sessions.push({
      timestamp: now.getTime(),
      hour,
      dayOfWeek: now.getDay()
    });

    // Keep only last 100 sessions
    if (this.sessions.length > 100) {
      this.sessions = this.sessions.slice(-100);
    }

    // Save to localStorage
    this.save();
  },

  /**
   * Get most active hours
   * @returns {number[]} Array of hours (0-23)
   */
  getActiveHours() {
    const hourCounts = Array(24).fill(0);

    this.sessions.forEach(session => {
      hourCounts[session.hour]++;
    });

    // Get top 3 hours
    const topHours = hourCounts
      .map((count, hour) => ({ hour, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 3)
      .map(item => item.hour);

    return topHours;
  },

  /**
   * Check if user is likely active now
   * @returns {boolean}
   */
  isLikelyActive() {
    const activeHours = this.getActiveHours();
    const currentHour = new Date().getHours();

    return activeHours.includes(currentHour);
  },

  /**
   * Save to localStorage
   */
  save() {
    try {
      localStorage.setItem('activitySessions', JSON.stringify(this.sessions));
    } catch { /* WKWebView ITP guard */ }
  },

  /**
   * Load from localStorage
   */
  load() {
    try {
      const saved = localStorage.getItem('activitySessions');
      if (saved) {
        this.sessions = JSON.parse(saved);
      }
    } catch { /* WKWebView ITP guard */ }
  }
};

// Load activity tracker on init
ActivityTracker.load();

export default {
  showNotification,
  showInsightNotification,
  requestNotificationPermission,
  hasNotificationSupport,
  getNotificationPermission,
  isQuietHours,
  checkNotificationThrottle,
  NotificationBadge,
  ActivityTracker
};
